if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.form || !this.submitButton) return;

        const variantIdInput = this.variantIdInput;
        if (!variantIdInput) return;

        variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.dataset.ajaxReady = 'true';
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const submittedVariantId = formData.get('id');
        let bundleItems = [];
        try {
          bundleItems = this.getSelectedBundleItems();
        } catch (error) {
          this.handleErrorMessage(error.message);
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        // Never clear the shopper's add-on opt-out on add-to-cart: an add-on switched
        // OFF in the cart drawer stays off until the cart is emptied.
        const defaultAddonItems = this.getDefaultCartAddons();

        // _pack_step: the quantity the shopper added in one go (Kaching Bundles
        // writes the deal size into the form's quantity input, e.g. "Buy 2 Get 1
        // Free" -> 3). The cart drawer steps +/- by this value and removes the
        // line when it would drop below one pack. Add-on lines never get it:
        // cart-drawer-upsell.js re-adds them with only _aw_cart_addon and a
        // different property set would split them into duplicate lines.
        const submittedQuantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);

        if (bundleItems.length || defaultAddonItems.length) {
          const mainItems = bundleItems.length
            ? bundleItems
            : [{
                id: String(formData.get('id')),
                quantity: submittedQuantity,
              }];
          const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          // Top-level line fields (properties[...] injected by Kaching Bundles,
          // selling_plan injected by Kaching Subscriptions) belong to the first
          // main item once the request switches to the items[] shape.
          const topLevelProperties = [];
          let topLevelSellingPlan = null;
          [...formData.entries()].forEach(([key, value]) => {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch) topLevelProperties.push([propertyMatch[1], value]);
            if (key === 'selling_plan' && value) topLevelSellingPlan = value;
          });
          [...formData.keys()].forEach((key) => {
            if (
              key === 'id' ||
              key === 'quantity' ||
              key === 'selling_plan' ||
              key.startsWith('options[') ||
              key.startsWith('properties[')
            ) {
              formData.delete(key);
            }
          });
          mainItems.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
            formData.append(`items[${index}][properties][_pack_step]`, String(item.quantity));
            if (bundleItems.length) {
              formData.append(`items[${index}][properties][_bundle_id]`, bundleId);
            }
            if (index === 0) {
              topLevelProperties.forEach(([name, value]) => {
                formData.append(`items[0][properties][${name}]`, value);
              });
              if (topLevelSellingPlan) formData.append('items[0][selling_plan]', topLevelSellingPlan);
            }
          });
          defaultAddonItems.forEach((item, addonIndex) => {
            const index = mainItems.length + addonIndex;
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, '1');
            formData.append(`items[${index}][properties][_aw_cart_addon]`, item.addonKey);
          });
        } else {
          formData.append('properties[_pack_step]', String(submittedQuantity));
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = bundleItems[0]?.id || submittedVariantId;
        const quantity = bundleItems.reduce((total, item) => total + item.quantity, 0) || submittedQuantity;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.cart = document.querySelector('cart-drawer');
              if (!this.cart) {
                this.handleErrorMessage('Cart drawer is temporarily unavailable. Please refresh and try again.');
                this.error = true;
                return;
              }
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
                renderedSections: this.cart?.tagName === 'CART-DRAWER' ? ['cart-drawer'] : [],
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getSelectedBundleItems() {
        const sectionId = this.dataset.sectionId;
        const quantityBreaks =
          document.querySelector(`quantity-breaks[data-section="${sectionId}"]`) ||
          document.getElementById(`quantity-breaks-${sectionId}`);
        if (!quantityBreaks) return [];

        const selectedInput = quantityBreaks.querySelector(
          '.quantity-breaks-container > input[type="radio"][name="quantity"]:checked'
        );
        if (!selectedInput) return [];

        const selectedCard = selectedInput.nextElementSibling;
        if (!selectedCard?.classList.contains('quantity-break')) return [];

        const requestedQuantity = Math.max(1, parseInt(selectedInput.value, 10) || 1);
        const selectors = [...selectedCard.querySelectorAll('.quantity-break__selector-item')];
        const variantDataElement = quantityBreaks.querySelector('script[type="application/json"]');
        const variants = variantDataElement ? JSON.parse(variantDataElement.textContent) : [];

        if (!selectors.length || !variants.length) {
          return [{ id: this.variantIdInput.value, quantity: requestedQuantity }];
        }

        const items = selectors.map((selector) => {
          const options = [...selector.querySelectorAll('.quantity-break__variant-select')].map(
            (select) => select.value
          );
          const variant = variants.find(
            (candidate) =>
              candidate.available &&
              candidate.options.length === options.length &&
              candidate.options.every((value, index) => value === options[index])
          );
          return variant ? { id: String(variant.id), quantity: 1 } : null;
        });

        if (items.some((item) => item === null)) {
          throw new Error('One or more selected bundle variants are unavailable.');
        }

        // Shopify only merges cart lines when the variant and line-item
        // properties are identical. Consolidate repeated selections here so a
        // bundle with the same color/size is added as one line with quantity N.
        return [...items.reduce((grouped, item) => {
          const id = String(item.id);
          const existing = grouped.get(id);
          if (existing) {
            existing.quantity += item.quantity;
          } else {
            grouped.set(id, { id, quantity: item.quantity });
          }
          return grouped;
        }, new Map()).values()];
      }

      getDefaultCartAddons({ resetOptOut = false } = {}) {
        const config = document.getElementById('AwDefaultCartAddons');
        if (!config) return [];

        const items = [...config.querySelectorAll('[data-id][data-addon-key]')];
        const readOptOut = (key) => {
          if (window.awAddonStore) return window.awAddonStore.get(key);
          try { return window.localStorage.getItem(key); } catch (e) {}
          try { return window.sessionStorage.getItem(key); } catch (e) {}
          return null;
        };
        if (resetOptOut) {
          items.forEach((item) => window.awAddonStore?.remove(item.dataset.storageKey));
        }

        return items
          .filter((item) => readOptOut(item.dataset.storageKey) !== 'off')
          .filter((item) => !document.querySelector(
            `cart-drawer-upsell[data-addon-key="${CSS.escape(item.dataset.addonKey)}"][data-selected="true"]`
          ))
          .map((item) => ({
            id: item.dataset.id,
            addonKey: item.dataset.addonKey,
          }));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
